import React, { Fragment, useMemo, useRef } from 'react';
import styles from './productHome.module.scss';
import i18n from 'i18n';
import { useProductHomeModel } from './ProductHomeModel';
import { Formik, FormikProps } from 'formik';
import { Form, NavDropdown } from 'react-bootstrap';
import { ProductFilter } from './ProductFilter';
import { FormikField } from 'components/form/field/FormikField';
import { notRoles } from 'core/permission/PermissionDSL';
import { RoleNames } from 'core';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ProductList } from './ProductList';
import { get, isEmpty, partial } from 'lodash';
import { InputField } from 'components/form/field/InputField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames/bind';
import { SelectField } from 'components/form/field/SelectField';

const cx = classNames.bind(styles);

export type ProductHomeProps = {
  defaultFilters?: ProductFilter;
  title?: string;
  renderButtons?: () => JSX.Element;
  singleSelect?: boolean;
};

export const ProductHome: React.FC<ProductHomeProps> = (props) => {

  const searchBarRef = useRef<any>(null);
  const {
    defaultFilters,
    title = i18n.t<string>('productHome.labels.title')
  } = props;

  const {
    loading,
    filters,
    minPrice,
    maxPrice,
    isAgency,
    vendorNumber,
    pagination,
    allProducts,
    productsData,
    advertiserOptions,
    vendorOptions,
    brandOptions,
    defaultOrderOptions,
    editActions,
    handleSearchString,
    handleMinPriceInput,
    handleMaxPriceInput,
    handleSubmit,
    onProductListChange,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    handleCategory1Change,
    handleCategory2Change,
    handleCategory3Change,
    handleCategory4Change,
    selectedFilterTypes,
    filterTypeOptions,
    handleAddFilterType,
    onVendorNumberChange,
    onBrandChange,
    onProductOwnerChange,
    onDefaultOrderChange,
    onStockTypeChange,
    stockTypeOptions
  } = useProductHomeModel(defaultFilters);

  const cannotSelectFilters = !get(filters, 'advertiser') || isEmpty(vendorNumber) || !get(filters, 'brand');

  const filterTypeElementMap: { [key: string]: JSX.Element } = useMemo(() => {
    const renderPriceMarginFilter = () => {
      const priceMarginClass = cx('fieldContent', 'priceMargin');
      return (
        <div className={priceMarginClass}>
          <InputField
            label={i18n.t<string>('productHome.filters.labels.priceMargin')}
            name='minPrice'
            fieldContentWidth={118}
            isFlexibleContent={true}
            value={minPrice ? minPrice.toString() : ''}
            onChange={handleMinPriceInput}
            className={styles.priceInput}
            type='number'
            min={1}
            disabled={cannotSelectFilters}
            placeholder={i18n.t<string>('productHome.placeholders.minPrice')}
          />
          <div className={styles.tilde}>~</div>
          <InputField
            name='maxPrice'
            fieldContentWidth={118}
            isFlexibleContent={true}
            value={maxPrice ? maxPrice.toString() : ''}
            onChange={handleMaxPriceInput}
            className={styles.priceInput}
            type='number'
            min={1}
            disabled={cannotSelectFilters}
            placeholder={i18n.t<string>('productHome.placeholders.maxPrice')}
          />
        </div>
      );
    };
    const renderDefaultOrderFilter = () => {
      return (
        <div className={styles.fieldContent}>
          <FormikField.Select
            label={i18n.t<string>('productHome.filters.labels.defaultOrder')}
            name='defaultOrder'
            options={defaultOrderOptions}
            fieldContentWidth={266}
            componentWidthFitParent={true}
            isFlexibleContent={true}
            simpleValue
            className={styles.productHomeSelect}
            disabled={cannotSelectFilters}
            onChange={onDefaultOrderChange}
          />
        </div>
      );
    };
    return {
      priceMargin: renderPriceMarginFilter(),
      defaultOrder: renderDefaultOrderFilter()
    };
  }, [cannotSelectFilters, minPrice, maxPrice, defaultOrderOptions, handleMinPriceInput, handleMaxPriceInput, onDefaultOrderChange]);

  const canSelectVendors = useMemo(() => {
    if (!isAgency) {
      return true;
    }
    return get(filters, 'advertiser');
  }, [isAgency, filters]);

  const canSelectBrands = useMemo(() => {
    if (!isAgency) {
      return true;
    }
    return get(filters, 'advertiser') && !isEmpty(vendorNumber);
  }, [isAgency, vendorNumber, filters]);

  const renderFilters = (formProps: FormikProps<ProductFilter>) => {
    const productHomeFilterAdderClass = cx('fieldContent', 'productHomeFilterAdder');
    const handleProductOwnerChange = (advertiser: number) => {
      onProductOwnerChange(advertiser);
      searchBarRef.current && searchBarRef.current.clear();
    };
    const renderFilterTypeList = () => {
      const items = filterTypeOptions.map(filterType => (
        <NavDropdown.Item
          key={filterType}
          onClick={partial(handleAddFilterType, filterType)}
        >
          {i18n.t<string>(`productHome.filters.labels.${filterType}`)}
        </NavDropdown.Item>
      ));
      return (
        filterTypeOptions.length > 0 ?
        <div className={productHomeFilterAdderClass}>
          <FormikField.Custom
            label=''
            name='productHomeFilterAdder'
            fieldContentWidth={266}
            isFlexibleContent={true}
          >
            <div className={styles.filterAdder}>
              <FontAwesomeIcon icon={faPlus} />
              <div className={styles.filterToAddList}>
                <NavDropdown title={i18n.t<string>('productHome.labels.filterCondition')} id='productHomeFilterList'>
                  {items}
                </NavDropdown>
              </div>
            </div>
          </FormikField.Custom>
        </div> : <div />
      );
    };
    return (
      <Form>
        <>
          {!get(defaultFilters, 'advertiser') && (
            <div className={styles.fieldContent}>
              <FormikField.Select
                label={i18n.t<string>('productHome.filters.labels.advertiser')}
                name='advertiser'
                options={advertiserOptions}
                fieldContentWidth={266}
                componentWidthFitParent={true}
                isFlexibleContent={true}
                simpleValue
                className={styles.productHomeSelect}
                onChange={handleProductOwnerChange}
              />
            </div>
          )}
          <div className={styles.fieldContent}>
            <SelectField
              label={i18n.t<string>('productHome.filters.labels.vendor')}
              name='vendorNumber'
              options={vendorOptions}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              className={styles.productHomeSelect}
              value={vendorNumber}
              onChange={onVendorNumberChange}
              disabled={!canSelectVendors}
            />
            <FormikField.Select
              name='brand'
              options={brandOptions}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              className={styles.productHomeSelect}
              onChange={onBrandChange}
              disabled={!canSelectBrands}
            />
          </div>
          <div className={styles.fieldContent}>
            <FormikField.Select
              label={i18n.t<string>('productHome.filters.labels.category')}
              name='category1'
              options={category1Options}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              onChange={handleCategory1Change}
              className={styles.productHomeSelect}
              isClearable={true}
              disabled={cannotSelectFilters}
            />
            <FormikField.Select
              name='category2'
              options={category2Options}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              onChange={partial(
                handleCategory2Change,
                formProps.values.category1
              )}
              className={styles.productHomeSelect}
              isClearable={true}
              disabled={cannotSelectFilters}
            />
            <FormikField.Select
              name='category3'
              options={category3Options}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              onChange={partial(
                handleCategory3Change,
                formProps.values.category1,
                formProps.values.category2
              )}
              className={styles.productHomeSelect}
              isClearable={true}
              disabled={cannotSelectFilters}
            />
            <FormikField.Select
              name='category4'
              options={category4Options}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              onChange={handleCategory4Change}
              className={styles.productHomeSelect}
              isClearable={true}
              disabled={cannotSelectFilters}
            />
          </div>
          <div className={styles.fieldContent}>
            <FormikField.Select
              label={i18n.t<string>('productHome.filters.labels.stockType')}
              name='stockType'
              options={stockTypeOptions}
              fieldContentWidth={266}
              componentWidthFitParent={true}
              isFlexibleContent={true}
              simpleValue
              className={styles.productHomeSelect}
              onChange={onStockTypeChange}
              isClearable={true}
              disabled={cannotSelectFilters}
            />
          </div>
          {selectedFilterTypes.map((filterType) => {
            return (
              <Fragment key={filterType}>
                {filterTypeElementMap[filterType]}
              </Fragment>
            );
          })}
          {renderFilterTypeList()}
        </>
      </Form>
    );
  };

  return (
    <>
      {loading && <LoadingIndicator />}
      <div className={styles.container}>
        <div className={styles.titleArea}>
          <div className={styles.title}>
            {title}
          </div>
          <PermissionChecker
            permissionAware={notRoles(RoleNames.sysAdmin, RoleNames.adsReport)}
          >
            <div className={styles.filtersFormContainer}>
              <Formik
                initialValues={filters}
                enableReinitialize
                onSubmit={handleSubmit}
                validateOnBlur={false}
              >
                {renderFilters}
              </Formik>
            </div>
          </PermissionChecker>
        </div>
        <ProductList
          filterSet={filters}
          productList={productsData}
          allProducts={allProducts}
          pagination={pagination}
          editActions={editActions()}
          onListChange={onProductListChange}
          onSearchStringChange={handleSearchString}
          singleSelect={props.singleSelect}
        />
        {props.renderButtons && (
          <div className={styles.buttonArea}>
            {props.renderButtons()}
          </div>
        )}
      </div>
    </>
  );
};
